/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { actions } from "../../actions";
import { services } from "../../services";
import { Record, Web3Data, RentalData } from "../../types/nft/create.rental";
// Shadcn UI components
import CustomLoader from "../ui/custom-loader";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

import { Input } from "../../components/ui/input";

import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { web3 } from "../../web3";
import { useToast } from "../../components/ui/use-toast";
import {
  sendTransactionHash,
  createRental,
} from "../../services/transactionApi";

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const CreateRental: React.FC<{ record: Record }> = ({ record }) => {
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [web3Data, setWeb3Data] = useState<Web3Data | null>(null);
  const [rentalContractInstance, setRentalContractInstance] =
    useState<any>(null);
  const [data, setData] = useState<RentalData>({
    assetAddress: record.assetAddress,
    pricePerDay: "",
    duration: 0,
    durationUnit: 0,
    allowpayrent: 0,
    rentalDescription: "",
    beds: 0,
    baths: 0,
    sqft: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [isRental, setIsRental] = useState(false);

  useEffect(() => {
    const initializeWeb3 = async () => {
      try {
        const web3Data = await services.getWeb3();
        if (web3Data) {
          setWeb3Data(web3Data as Web3Data);
        }
      } catch (error) {
        setError(
          "Failed to initialize Web3. Please check your wallet connection.",
        );
      }
    };

    const initializeContract = async () => {
      try {
        const instance = await actions.getRentalPoolContract();
        setRentalContractInstance(instance);
        const rentalCheck = await instance.methods
          .isPropertyListed(record.assetAddress)
          .call();
        setIsRental(Boolean(rentalCheck));
      } catch (error) {
        setError(
          "Failed to initialize rental contract. Please try again later.",
        );
      }
    };

    initializeWeb3();
    initializeContract();
  }, []);

  const handleOpenDialog = async () => {
    if (!web3Data || !web3Data.accounts || web3Data.accounts.length === 0) {
      setError(
        "Web3 data or accounts not available. Please connect your wallet.",
      );

      return;
    }

    const owner = await rentalContractInstance.methods.owner().call();

    if (owner !== web3Data.accounts[0]) {
      toast({
        description: "You haven't logged in with the owner account.",

        variant: "destructive",
      });

      return;
    }
    setShowDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setError(null);
  };

  const handleCreateRental = async (): Promise<void> => {
    if (!web3Data || !web3Data.accounts || web3Data.accounts.length === 0) {
      setError(
        "Web3 data or accounts not available. Please connect your wallet.",
      );
      return;
    }

    setLoading(true);
    setError(null);

    if (!data.pricePerDay) {
      setError("Price per day is required");
      setLoading(false);
      return;
    }

    let price;
    try {
      //@ts-ignore
      price = web3.utils.toWei(data.pricePerDay, "ether");
      if (
        !rentalContractInstance ||
        !data.duration ||
        data.durationUnit === null ||
        !data.rentalDescription ||
        !data.beds ||
        !data.baths ||
        !data.sqft
      ) {
        throw new Error("Missing required rental data");
      }

      await rentalContractInstance.methods
        .listPropertyForRent(
          data.assetAddress,
          data.duration,
          data.durationUnit,
          price,
          data.allowpayrent,
        )
        .send({
          from: web3Data.accounts[0],
        })
        .on("transactionHash", async (hash) => {
          try {
            const apiResponse = await sendTransactionHash(hash);
          } catch (apiError) {}
        })
        .on("receipt", async (receipt: any) => {
          // Using browser alert instead of react-admin notify
          toast({
            description: "Rental created successfully",
            variant: "default",
          });

          // Wait for 2 seconds (2000 ms)

          await delay(2000);

          // Now, call the second API to add the description

          const payload = {
            txHash: receipt.transactionHash, // The ID to find the record

            rentalDescription: data.rentalDescription, // The data to update
            beds: data.beds,
            baths: data.baths,
            sqft: data.sqft,
          };

          try {
            await createRental(payload);
          } catch (error) {
            toast({
              description: "Failed to save rental description",

              variant: "destructive",
            });
          }
        })

        .on("error", async (error) => {
          // Optionally send error info to backend

          const baseRentalData = {
            assetAddress: data.assetAddress,

            rentalDescription: data.rentalDescription,
            beds: data.beds,
            baths: data.baths,
            sqft: data.sqft,

            duration: data.duration,

            durationUnit: data.durationUnit,

            pricePerDay: data.pricePerDay,

            allowpayrent: data.allowpayrent,

            listedBy: web3Data.accounts[0],
          };

          const rentalData = {
            ...baseRentalData,

            txHash: "", // or undefined

            status: "REJECTED",

            error: error.message,
          };

          try {
            await createRental(rentalData);
          } catch (err) {
            // handle error
          }

          toast({
            description: "Rental transaction failed",

            variant: "destructive",
          });
        });
      setShowDialog(false);
    } catch (error: any) {
      setError(error.message || "Error creating rental");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpenDialog}
        disabled={loading || !isRental}
        variant="outline"
        className="bg-surface-2 border-surface-2 hover:bg-surface-2 text-surface-2"
      >
        {loading ? (
          <>
            <CustomLoader size="sm" className="mr-2 h-4 w-4 animate-spin" />
            Processing
          </>
        ) : (
          "Rent Now"
        )}
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create Rental</DialogTitle>
            <DialogDescription>
              Set up rental details for this asset
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="pricePerDay">Rental Amount</Label>
              <Input
                id="pricePerDay"
                placeholder=""
                value={data.pricePerDay}
                onChange={(e) =>
                  setData({ ...data, pricePerDay: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="duration">Rent Duration</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="duration"
                  type="number"
                  className="w-1/2 h-10"
                  value={data.duration}
                  onChange={(e) =>
                    setData({ ...data, duration: parseInt(e.target.value) })
                  }
                />
                <Select
                  value={data.durationUnit.toString()}
                  onValueChange={(value) =>
                    setData({ ...data, durationUnit: parseInt(value) })
                  }
                >
                  <SelectTrigger className="w-1/2 h-10 px-3 py-2">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Days</SelectItem>
                    <SelectItem value="1">Months</SelectItem>
                    <SelectItem value="2">Years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="duration">Rental Distributor</Label>

              <div className="flex items-center gap-2">
                <Select
                  value={data.allowpayrent.toString()}
                  onValueChange={(value) =>
                    setData({ ...data, allowpayrent: parseInt(value) })
                  }
                >
                  <SelectTrigger className="w-1/2 h-10 px-3 py-2">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="1">Tenant</SelectItem>

                    <SelectItem value="0">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="rentalDescription">Rental Description</Label>

              <Input
                id="rentalDescription"
                placeholder="Enter rental description"
                value={data.rentalDescription}
                onChange={(e) =>
                  setData({ ...data, rentalDescription: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="beds">Beds</Label>
              <Input
                id="beds"
                type="number"
                value={data.beds}
                onChange={(e) =>
                  setData({ ...data, beds: parseInt(e.target.value) })
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="baths">Baths</Label>
              <Input
                id="baths"
                type="number"
                value={data.baths}
                onChange={(e) =>
                  setData({ ...data, baths: parseInt(e.target.value) })
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="sqft">Square Feet</Label>
              <Input
                id="sqft"
                type="number"
                value={data.sqft}
                onChange={(e) =>
                  setData({ ...data, sqft: parseInt(e.target.value) })
                }
              />
            </div>

            {/* {error && (
              <div className="text-destructive text-sm mt-2">{error}</div>
            )} */}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleCreateRental} disabled={loading}>
              {loading ? (
                <>
                  <CustomLoader
                    size="sm"
                    className="mr-2 h-4 w-4 animate-spin"
                  />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateRental;
